import { makeStyles } from '@material-ui/core';
import { useState, useEffect } from 'react';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import RoundedButton from 'components/Shared/RoundedButton';
import Modal from 'components/Shared/Modal';
import UserEditForm from '../../UserEditForm';
import { useDispatch, useSelector } from 'react-redux';
import { editUserData } from 'redux_store/api/endpoints/users';
import { resetEditUserStatus } from 'redux_store/slices/userAccountManagementSlice';
import { useParams } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  modal: {
    width: '100%',
    minWidth: 600,
    maxWidth: '90vw',
    height: 'auto',
    maxHeight: '80vh',
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(4, 3),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    width: '100%',
  },
  actionsContainer: {
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    width: '100%',
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
  },
}));

const UserEditModal = ({
                         open,
                         handleClose,
                         userData,
                         onSave,
                         accountUuid,
                       }) => {
  const classes = useStyles();
  const { currentAccountUuid } = useParams();
  const { en: { titles, btn } } = useSelector((state) => state.i18n);
  const { editUserStatus } = useSelector((state) => state.userAccountManagement);
  const [backendErrors, setBackendErrors] = useState({});
  const dispatch = useDispatch();
  const finalAccountUuid = accountUuid || currentAccountUuid;

  // Auto-reset failed status after 2 seconds
  useEffect(() => {
    if (editUserStatus === 'failed') {
      const timeout = setTimeout(() => {
        dispatch(resetEditUserStatus());
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [editUserStatus, dispatch]);

  const closeModal = (hasUserBeenUpdated) => {
    handleClose(hasUserBeenUpdated);
    setBackendErrors({});
    dispatch(resetEditUserStatus());
  };

  const editUser = async (values, { setSubmitting }) => {
    try {
      // Reset states
      setBackendErrors({});

      let result;
      // Edit existing user
      if (!userData?.uuid || !finalAccountUuid) {
        setSubmitting(false);
        return;
      }

      result = await dispatch(editUserData({
        currentAccountUuid: finalAccountUuid,
        userUuid: userData.uuid,
        formValues: values,
      }));

      if (editUserData.fulfilled.match(result)) {
        // Success - call parent's onSave function and close modal
        if (onSave) {
          onSave(values);
        }

        // Close modal after a short delay for better UX
        setTimeout(() => {
          closeModal(true);
        }, 500);
      } else if (editUserData.rejected.match(result)) {
        // Handle validation errors
        if (result.payload?.validationErrors) {
          setBackendErrors(result.payload.validationErrors);
        }
      }
    } catch (error) {
      // Error is handled by Redux thunk
    }
    setSubmitting(false);
  };

  const initialValues = {
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    email: userData?.email || '',
    phone: userData?.phone || '',
  };

  return (
    <Modal
      open={open}
      classes={{ paper: classes.modal }}
      title={titles.editUser || 'Edit User'}
      handleClose={() => closeModal(false)}
    >
      <Formik
        initialValues={initialValues}
        validateOnBlur={false}
        enableReinitialize
        onSubmit={editUser}
      >
        {({ isValid, isSubmitting, dirty, handleSubmit, values }) => {
          const currentStatus = editUserStatus;
          const isLoading = isSubmitting || currentStatus === 'loading';
          const isSuccess = currentStatus === 'succeeded';
          const isFailed = currentStatus === 'failed';

          return (
            <>
              <div className={classes.scrollableContent}>
                <UserEditForm
                  values={values}
                  backendErrors={backendErrors}
                />
              </div>
              <div className={classes.actionsContainer}>
                <RoundedButton
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={() => closeModal(false)}
                  className={classes.actionButton}
                  disabled={isLoading}
                >
                  {btn.cancel || 'Cancel'}
                </RoundedButton>
                <RoundedButton
                  variant="contained"
                  color="primary"
                  size="medium"
                  disabled={!isValid || isLoading || !dirty}
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  isSuccess={isSuccess}
                  isFailed={isFailed}
                  className={classes.actionButton}
                >
                  {btn.save || 'Save'}
                </RoundedButton>
              </div>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};

UserEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  userData: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  userUuid: PropTypes.string,
  accountUuid: PropTypes.string,
  onUserCreated: PropTypes.func,
};

UserEditModal.defaultProps = {
  userData: {},
  onSave: null,
  userUuid: null,
  accountUuid: null,
  onUserCreated: null,
};

export default UserEditModal;
